"use client";

import { GameControlsMute } from "@workspace/frontend-ui";
import { useEffect, useLayoutEffect, useRef, useState } from "react";

const STILL_SLIDE_DURATION_MS = 8000;
const VISIBLE_ENOUGH_FOR_SOUND = 0.5;
const VIDEO_EXTENSION = /\.(mp4|webm|mov|m4v|ogv)(\?.*)?$/i;

const isVideoUrl = (url: string) => VIDEO_EXTENSION.test(url);

type Props = {
  videoUrl?: string;
  thumbnailUrl?: string;
  alt: string;
  isActive: boolean;
  translations: Record<string, string>;
  onProgress?: (progress: number) => void;
  onComplete?: () => void;
};

const HeroMedia: FC<Props> = ({
  videoUrl,
  thumbnailUrl,
  alt,
  isActive,
  translations,
  onProgress,
  onComplete,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isMuted, setIsMuted] = useState(true);
  const [isInView, setIsInView] = useState(true);
  const [isPageVisible, setIsPageVisible] = useState(true);
  const isWatched = isInView && isPageVisible;
  const [canPlay, setCanPlay] = useState(true);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) {
      return;
    }
    if (isActive && isWatched) {
      video.currentTime = 0;
      video.play().then(
        () => setCanPlay(true),
        () => setCanPlay(false),
      );
      return;
    }
    video.pause();
  }, [isActive, isWatched]);

  useEffect(() => {
    if (!isActive || !isWatched || !onProgress || !onComplete) {
      return;
    }

    const video = videoRef.current;
    const startedAt = performance.now();
    let lastTime = 0;
    let lastAdvancedAt = startedAt;
    let frame = requestAnimationFrame(function tick() {
      if (video && canPlay) {
        const { currentTime, duration } = video;
        onProgress(duration > 0 ? Math.min(1, currentTime / duration) : 0);
        if (currentTime < lastTime - 0.5) {
          onComplete();
        }
        if (currentTime > lastTime) {
          lastAdvancedAt = performance.now();
        }
        lastTime = currentTime;
        if (performance.now() - lastAdvancedAt > STILL_SLIDE_DURATION_MS) {
          onComplete();
          return;
        }
      } else {
        const elapsed =
          (performance.now() - startedAt) / STILL_SLIDE_DURATION_MS;
        onProgress(Math.min(1, elapsed));
        if (elapsed >= 1) {
          onComplete();
          return;
        }
      }
      frame = requestAnimationFrame(tick);
    });

    return () => cancelAnimationFrame(frame);
  }, [isActive, canPlay, isWatched, onProgress, onComplete]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) {
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsInView(entry.intersectionRatio >= VISIBLE_ENOUGH_FOR_SOUND);
      },
      { threshold: VISIBLE_ENOUGH_FOR_SOUND },
    );
    observer.observe(video);
    return () => observer.disconnect();
  }, [videoUrl]);

  useEffect(() => {
    const syncPageVisible = () => setIsPageVisible(!document.hidden);
    syncPageVisible();
    document.addEventListener("visibilitychange", syncPageVisible);
    return () =>
      document.removeEventListener("visibilitychange", syncPageVisible);
  }, []);

  useLayoutEffect(() => {
    const video = videoRef.current;
    return () => {
      if (!video) {
        return;
      }
      video.pause();
      video.muted = true;
      setIsMuted(true);
    };
  }, [videoUrl]);

  useEffect(() => {
    if (isWatched) {
      return;
    }
    setIsMuted(true);
    if (videoRef.current) {
      videoRef.current.muted = true;
    }
  }, [isWatched]);

  const handleToggleMute = () => {
    const nextMuted = !isMuted;
    setIsMuted(nextMuted);
    if (videoRef.current) {
      videoRef.current.muted = nextMuted;
    }
  };

  if (!videoUrl) {
    return (
      <img
        src={thumbnailUrl}
        alt={alt}
        className="absolute inset-0 size-full object-cover"
      />
    );
  }

  if (!isVideoUrl(videoUrl)) {
    return (
      <img
        src={videoUrl}
        alt={alt}
        className="absolute inset-0 size-full object-cover"
      />
    );
  }

  return (
    <>
      <img
        src={thumbnailUrl}
        alt=""
        className="absolute inset-0 size-full object-cover"
      />
      <video
        ref={videoRef}
        src={videoUrl}
        poster={thumbnailUrl}
        muted
        loop
        playsInline
        aria-label={alt}
        onClick={handleToggleMute}
        className="absolute inset-0 size-full cursor-pointer object-cover object-top"
      />
      <GameControlsMute
        isMuted={isMuted}
        iconSize={16}
        onClick={handleToggleMute}
        aria-label={
          translations[
            isMuted
              ? "widget.highlighted-games.unmute"
              : "widget.highlighted-games.mute"
          ]
        }
        className="absolute top-4 right-4 z-10 size-8 rounded-lg bg-secondary"
      />
    </>
  );
};

export default HeroMedia;
